import { useState } from 'react';

import { PublicationList, PublicationRow } from '@/components/publications/PublicationRow';
import { ReleveSection } from '@/components/publications/ReleveSection';
import { Accent } from '@/components/ui/Accent';
import { ButtonLink } from '@/components/ui/ButtonLink';
import { Container } from '@/components/ui/Container';
import { Label } from '@/components/ui/Label';
import { Section } from '@/components/ui/Section';
import { latestReleve, publications, type PublicationKind } from '@/content/publications';
import { site } from '@/content/site';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { cn } from '@/lib/cn';

const FILTERS: { id: PublicationKind | 'all'; label: string }[] = [
  { id: 'all', label: 'Tout' },
  { id: 'episode', label: 'Épisodes' },
  { id: 'releve', label: 'Le Relevé' },
  { id: 'note', label: 'Notes' },
];

export function BlogPage() {
  useDocumentMeta({
    title: `Blog, ${site.name}`,
    description:
      'Les articles de chaque épisode, le Relevé hebdomadaire produit par le pipeline et relu à la main, et des notes plus courtes sur les choix faits en route.',
    path: '/blog',
  });

  const [filter, setFilter] = useState<PublicationKind | 'all'>('all');
  const shown = publications.filter((publication) => filter === 'all' || publication.kind === filter);

  return (
    <>
      <section aria-labelledby="blog-titre" className="pt-13 pb-6 tab:pt-21 tab:pb-10">
        <Container>
          <Label>Blog</Label>
          <h1 id="blog-titre" className="mt-5.5 text-title">
            Les textes, le Relevé,
            <br />
            <Accent text="les |notes." />
          </h1>
          <p className="mt-4.5 max-w-[40em] text-sub text-encre-soft">
            Les articles de chaque épisode, le Relevé hebdomadaire produit par le pipeline et relu à
            la main, et des notes plus courtes sur les choix faits en route.
          </p>

          <div role="group" aria-label="Filtrer par type" className="mt-9 flex flex-wrap gap-2">
            {FILTERS.map((item) => (
              <button
                key={item.id}
                type="button"
                aria-pressed={filter === item.id}
                onClick={() => setFilter(item.id)}
                className={cn(
                  'label min-h-10 cursor-pointer rounded-pill border-0 px-4 py-3 transition-colors',
                  filter === item.id ? 'bg-encre text-blanc' : 'bg-blanc text-encre hover:bg-blanc/70',
                )}
              >
                {item.label}
              </button>
            ))}
          </div>
        </Container>
      </section>

      <Section className="pt-0 tab:pt-0" aria-label="Publications">
        <Container>
          <PublicationList>
            {shown.map((publication) => (
              <PublicationRow
                key={`${publication.kind}-${publication.date}`}
                publication={publication}
                withIcon
                withDetail
              />
            ))}
          </PublicationList>
        </Container>
      </Section>

      {latestReleve && (
        <ReleveSection
          releve={latestReleve}
          eyebrow={`Le Relevé, semaine ${latestReleve.semaine}`}
          action={
            // Flux RSS à brancher : la maquette le prévoit, il n'existe pas encore.
            <ButtonLink to="#" icon="rss">
              S&apos;abonner au flux
            </ButtonLink>
          }
        />
      )}
    </>
  );
}
